/**
 * Configuration — Flagging: options, catalogs, samples, normalization.
 */

export const FLAG_TYPE_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'post', label: 'Post' },
  { value: 'review', label: 'Review' },
  { value: 'message', label: 'Message' },
  { value: 'quote', label: 'Quote' },
];

export const FLAGGING_SUBMITTED_BY_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'business', label: 'Business' },
];

export const FLAGGING_AVAILABILITY_OPTIONS = [
  { value: 'signed_in_accounts_only', label: 'Signed-In Accounts Only' },
  { value: 'available_after_valid_interaction', label: 'Available After Valid Interaction' },
  { value: 'own_conversation_only', label: 'Own Conversation Only' },
  { value: 'own_quote_thread_only', label: 'Own Quote Thread Only' },
];

const AVAILABILITY_BY_FLAG_TYPE = {
  general: ['signed_in_accounts_only'],
  post: ['signed_in_accounts_only', 'available_after_valid_interaction'],
  review: ['signed_in_accounts_only', 'available_after_valid_interaction'],
  message: ['own_conversation_only'],
  quote: ['own_quote_thread_only'],
};

export const FLAG_REASON_SET_OPTIONS = [
  { value: 'general_reasons', label: 'General Reasons', flagType: 'general' },
  { value: 'post_reasons', label: 'Post Reasons', flagType: 'post' },
  { value: 'message_reasons', label: 'Message Reasons', flagType: 'message' },
  { value: 'review_reasons', label: 'Review Reasons', flagType: 'review' },
  { value: 'quote_reasons', label: 'Quote Reasons', flagType: 'quote' },
];

const REASON_ITEMS_BY_SET = {
  general_reasons: [
    { value: 'spam', label: 'Spam' },
    { value: 'misleading', label: 'Misleading Content' },
    { value: 'offensive', label: 'Offensive Content' },
    { value: 'other', label: 'Other' },
  ],
  post_reasons: [
    { value: 'spam', label: 'Spam' },
    { value: 'misleading', label: 'Misleading Content' },
    { value: 'scam', label: 'Scam or Suspicious Content' },
    { value: 'offensive', label: 'Offensive Content' },
    { value: 'copyright', label: 'Copyright Issues' },
    { value: 'profanity', label: 'Profanity Issues' },
    { value: 'other', label: 'Other' },
  ],
  message_reasons: [
    { value: 'abuse', label: 'Abuse' },
    { value: 'scam', label: 'Scam' },
    { value: 'spam', label: 'Spam' },
    { value: 'contact_misuse', label: 'Contact Misuse' },
  ],
  review_reasons: [
    { value: 'fake', label: 'Fake Review' },
    { value: 'abusive_language', label: 'Abusive Language' },
    { value: 'defamatory', label: 'Defamatory Content' },
    { value: 'spam', label: 'Spam' },
    { value: 'conflict', label: 'Conflict of Interest' },
    { value: 'irrelevant', label: 'Irrelevant Review' },
    { value: 'other', label: 'Other' },
  ],
  quote_reasons: [
    { value: 'spam', label: 'Spam' },
    { value: 'scam', label: 'Scam or Suspicious Content' },
    { value: 'misleading', label: 'Misleading Content' },
    { value: 'contact_misuse', label: 'Contact Misuse' },
    { value: 'other', label: 'Other' },
  ],
};

const BLOCK_ELIGIBLE_MESSAGE_REASONS = ['abuse', 'scam', 'spam', 'contact_misuse'];

export const FLAG_DETAIL_REQUIREMENT_OPTIONS = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'optional', label: 'Optional' },
  { value: 'required_selected', label: 'Required for Selected Reasons' },
];

export const DUPLICATE_CHECK_OPTIONS = [
  { value: 'allow_another_flag', label: 'Allow Another Flag' },
  { value: 'block_active_duplicate', label: 'Block Active Duplicate' },
];

export const BLOCK_OPTION_OPTIONS = [
  { value: 'not_available', label: 'Not Available' },
  { value: 'available_selected_reasons', label: 'Available for Selected Reasons' },
];

export const SUBMISSION_RESULT_OPTIONS = [
  { value: 'confirmation_only', label: 'Confirmation Only' },
  { value: 'confirmation_with_review_message', label: 'Confirmation with Review Message' },
];

const LEGACY_TYPE_FROM_SURFACE = {
  post: 'post',
  message: 'message',
  review: 'review',
  review_reply: 'review',
  general: 'general',
  quote: 'quote',
};

const LEGACY_AVAILABILITY_MAP = {
  always_available: 'signed_in_accounts_only',
  signed_in_only: 'signed_in_accounts_only',
  after_interaction: 'available_after_valid_interaction',
  own_message_record_only: 'own_conversation_only',
};

const LEGACY_DUPLICATE_MAP = {
  allowed: 'allow_another_flag',
  block_during_cooldown: 'block_active_duplicate',
};

const LEGACY_BLOCK_MAP = {
  available_in_flow: 'available_selected_reasons',
};

const LEGACY_SUBMISSION_MAP = {
  confirmation_logged: 'confirmation_with_review_message',
};

const SPECIFIC_FLAG_TYPES = ['post', 'review', 'message', 'quote'];

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  const found = options.find((o) => o.value === value);
  return found ? found.label : String(value);
}

export function labelsFromValues(options, values) {
  if (!Array.isArray(values) || values.length === 0) return [];
  return values.map((v) => labelFromOptions(options, v)).filter(Boolean);
}

export function getFlagTypeLabel(value) {
  return labelFromOptions(FLAG_TYPE_OPTIONS, normalizeFlagType(value));
}

export function normalizeFlagType(raw) {
  const v = String(raw || '').toLowerCase().trim();
  if (LEGACY_TYPE_FROM_SURFACE[v]) return LEGACY_TYPE_FROM_SURFACE[v];
  if (FLAG_TYPE_OPTIONS.some((o) => o.value === v)) return v;
  return '';
}

export function getAvailabilityOptionsForFlagType(flagType) {
  const type = normalizeFlagType(flagType);
  const allowed = AVAILABILITY_BY_FLAG_TYPE[type] || [];
  return FLAGGING_AVAILABILITY_OPTIONS.filter((o) => allowed.includes(o.value));
}

export function getFlagReasonSetOptions(flagType) {
  const type = normalizeFlagType(flagType);
  if (!type) return [];
  return FLAG_REASON_SET_OPTIONS.filter((o) => o.flagType === type);
}

export function getReasonItemsForSet(reasonSet) {
  return REASON_ITEMS_BY_SET[reasonSet] || [];
}

export function getDetailRequiredReasonOptions(reasonSet) {
  return getReasonItemsForSet(reasonSet);
}

export function getBlockOfferingReasonOptions(reasonSet) {
  if (reasonSet !== 'message_reasons') return [];
  return getReasonItemsForSet(reasonSet).filter((o) => BLOCK_ELIGIBLE_MESSAGE_REASONS.includes(o.value));
}

export function supportsBlockOption(flagType) {
  return normalizeFlagType(flagType) === 'message';
}

export function needsDetailRequiredReasons(flagDetailRequirement) {
  return flagDetailRequirement === 'required_selected';
}

export function needsBlockOfferingReasons(blockOption) {
  return blockOption === 'available_selected_reasons';
}

export function getFlagReasonSetLabel(value) {
  return labelFromOptions(FLAG_REASON_SET_OPTIONS, value);
}

export function normalizeFlaggingRuleStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'draft' || s === 'active' || s === 'inactive') return s;
  return 'draft';
}

function inferReasonSet(flagType, rawSet) {
  if (typeof rawSet === 'string' && FLAG_REASON_SET_OPTIONS.some((o) => o.value === rawSet)) return rawSet;
  const legacyReasons = Array.isArray(rawSet) ? rawSet : [];
  const type = normalizeFlagType(flagType);
  const match = FLAG_REASON_SET_OPTIONS.find((o) => o.flagType === type);
  if (match && !legacyReasons.length) return match.value;
  if (legacyReasons.length) {
    const entries = Object.entries(REASON_ITEMS_BY_SET);
    for (const [setKey, items] of entries) {
      const itemValues = items.map((i) => i.value);
      if (legacyReasons.every((r) => itemValues.includes(r))) return setKey;
    }
  }
  return match?.value || '';
}

export function normalizeFlaggingForm(raw = {}) {
  const flagType = normalizeFlagType(raw.flagType || raw.surface);
  const flagReasonSet = inferReasonSet(flagType, raw.flagReasonSet);
  const flagDetailRequirement = raw.flagDetailRequirement || '';
  const blockOptionRaw = LEGACY_BLOCK_MAP[raw.blockOption] || raw.blockOption || '';
  const blockOption = supportsBlockOption(flagType)
    ? blockOptionRaw
    : 'not_available';
  const allowedDetailReasons = new Set(getReasonItemsForSet(flagReasonSet).map((o) => o.value));
  const allowedBlockReasons = new Set(getBlockOfferingReasonOptions(flagReasonSet).map((o) => o.value));

  return {
    id: raw.id || '',
    ruleName: raw.ruleName || '',
    flagType,
    submittedBy: Array.isArray(raw.submittedBy) ? [...raw.submittedBy] : [],
    flaggingAvailability: LEGACY_AVAILABILITY_MAP[raw.flaggingAvailability] || raw.flaggingAvailability || '',
    flagReasonSet,
    flagDetailRequirement,
    detailRequiredReasons: needsDetailRequiredReasons(flagDetailRequirement)
      ? (raw.detailRequiredReasons || []).filter((v) => allowedDetailReasons.has(v))
      : [],
    duplicateCheck: LEGACY_DUPLICATE_MAP[raw.duplicateCheck] || raw.duplicateCheck || '',
    blockOption,
    blockOfferingReasons: needsBlockOfferingReasons(blockOption)
      ? (raw.blockOfferingReasons || []).filter((v) => allowedBlockReasons.has(v))
      : [],
    submissionResult: LEGACY_SUBMISSION_MAP[raw.submissionResult] || raw.submissionResult || '',
    status: normalizeFlaggingRuleStatus(raw.status),
    version: raw.version ?? 1,
    pendingDraft: raw.pendingDraft ?? null,
    configurationHistory: raw.configurationHistory || [],
    testHistory: raw.testHistory || [],
    createdOn: raw.createdOn || '',
    createdBy: raw.createdBy || '',
    modifiedOn: raw.modifiedOn || '',
    modifiedBy: raw.modifiedBy || '',
    _editingActiveRevision: raw._editingActiveRevision ?? false,
  };
}

export function nextFlaggingRuleId(allRules) {
  const nums = (allRules || [])
    .map((r) => parseInt(String(r.id || '').replace(/^FLG-/i, ''), 10))
    .filter((n) => Number.isFinite(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `FLG-${String(next).padStart(4, '0')}`;
}

function ruleSignature(rule) {
  const n = normalizeFlaggingForm(rule);
  const submittedBy = [...(n.submittedBy || [])].sort().join('|');
  return [
    n.flagType,
    submittedBy,
    n.flaggingAvailability,
    n.flagReasonSet,
    n.duplicateCheck,
    n.blockOption,
    n.submissionResult,
  ].join(':');
}

export function findDuplicateFlaggingRule(allRules, candidate, excludeId) {
  const sig = ruleSignature(candidate);
  return (allRules || []).find((r) => {
    if (excludeId && r.id === excludeId) return false;
    if (normalizeFlaggingRuleStatus(r.status) !== 'active') return false;
    return ruleSignature(r) === sig;
  });
}

export function findConflictingFlaggingRule(allRules, candidate, excludeId) {
  const n = normalizeFlaggingForm(candidate);
  return (allRules || []).find((r) => {
    if (excludeId && r.id === excludeId) return false;
    if (normalizeFlaggingRuleStatus(r.status) !== 'active') return false;
    const o = normalizeFlaggingForm(r);
    if (o.flagType !== n.flagType) {
      if (n.flagType === 'general' && SPECIFIC_FLAG_TYPES.includes(o.flagType)) return true;
      if (o.flagType === 'general' && SPECIFIC_FLAG_TYPES.includes(n.flagType)) return true;
      return false;
    }
    const sameSubmitters = [...(o.submittedBy || [])].sort().join('|') === [...(n.submittedBy || [])].sort().join('|');
    if (!sameSubmitters) return false;
    if (
      o.flaggingAvailability !== n.flaggingAvailability
      || o.flagReasonSet !== n.flagReasonSet
      || o.flagDetailRequirement !== n.flagDetailRequirement
      || o.duplicateCheck !== n.duplicateCheck
      || o.blockOption !== n.blockOption
      || o.submissionResult !== n.submissionResult
    ) {
      return true;
    }
    return false;
  });
}

function baseSample(overrides) {
  return normalizeFlaggingForm({
    status: 'draft',
    version: 1,
    createdOn: '2025-01-15T09:00:00.000Z',
    createdBy: 'System Admin',
    modifiedOn: '2025-01-15T09:00:00.000Z',
    modifiedBy: 'System Admin',
    ...overrides,
  });
}

export function buildSampleFlaggingRules() {
  return [
    baseSample({
      id: 'FLG-0001',
      ruleName: 'Post Flagging Rule',
      flagType: 'post',
      submittedBy: ['customer', 'business'],
      flaggingAvailability: 'signed_in_accounts_only',
      flagReasonSet: 'post_reasons',
      flagDetailRequirement: 'optional',
      duplicateCheck: 'block_active_duplicate',
      blockOption: 'not_available',
      submissionResult: 'confirmation_with_review_message',
    }),
    baseSample({
      id: 'FLG-0002',
      ruleName: 'Message Flagging Rule',
      flagType: 'message',
      submittedBy: ['customer', 'business'],
      flaggingAvailability: 'own_conversation_only',
      flagReasonSet: 'message_reasons',
      flagDetailRequirement: 'optional',
      duplicateCheck: 'block_active_duplicate',
      blockOption: 'available_selected_reasons',
      blockOfferingReasons: ['abuse', 'scam', 'spam'],
      submissionResult: 'confirmation_with_review_message',
    }),
    baseSample({
      id: 'FLG-0003',
      ruleName: 'Review Flagging Rule',
      flagType: 'review',
      submittedBy: ['customer', 'business'],
      flaggingAvailability: 'available_after_valid_interaction',
      flagReasonSet: 'review_reasons',
      flagDetailRequirement: 'required_selected',
      detailRequiredReasons: ['fake', 'abusive_language'],
      duplicateCheck: 'block_active_duplicate',
      blockOption: 'not_available',
      submissionResult: 'confirmation_with_review_message',
    }),
    baseSample({
      id: 'FLG-0004',
      ruleName: 'General Flagging Rule',
      flagType: 'general',
      submittedBy: ['customer', 'business'],
      flaggingAvailability: 'signed_in_accounts_only',
      flagReasonSet: 'general_reasons',
      flagDetailRequirement: 'not_required',
      duplicateCheck: 'block_active_duplicate',
      blockOption: 'not_available',
      submissionResult: 'confirmation_only',
    }),
    baseSample({
      id: 'FLG-0005',
      ruleName: 'Quote Flagging Rule',
      flagType: 'quote',
      submittedBy: ['customer', 'business'],
      flaggingAvailability: 'own_quote_thread_only',
      flagReasonSet: 'quote_reasons',
      flagDetailRequirement: 'optional',
      duplicateCheck: 'block_active_duplicate',
      blockOption: 'not_available',
      submissionResult: 'confirmation_with_review_message',
    }),
  ];
}

/** @deprecated */
export const FLAGGING_SURFACE_OPTIONS = FLAG_TYPE_OPTIONS;

/** @deprecated */
export function availabilityOptionsForSurface(surface) {
  return getAvailabilityOptionsForFlagType(surface);
}

/** @deprecated */
export function reasonOptionsForSurface(surface) {
  const type = normalizeFlagType(surface);
  const set = getFlagReasonSetOptions(type)[0]?.value;
  return getReasonItemsForSet(set);
}

/** @deprecated */
export const BLOCK_OPTION_CHOICES = BLOCK_OPTION_OPTIONS;

/** @deprecated */
export const CASE_CREATION_OPTIONS = [];

/** @deprecated */
export function flaggingDuplicateFingerprint(r) {
  return ruleSignature(r);
}
